'use client';

import { useState, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';

import Image from '../image';
import Diamond from '../icon/diamond';
import Modal from '../modal';
import BevSafeText from '../bev-safe-text';
import type { ItineraryStepData } from './types';

export type Props = {
	steps: ItineraryStepData[];
	title?: string;
	className?: string;
};

const MIN_STEPS = 3;

export default function CompactItinerary({
	steps,
	title = 'Itinerary',
	className,
}: Props): ReactElement | null {
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
	const t = useTranslations();

	if (steps.length < MIN_STEPS) {
		return null;
	}

	const showImages = steps.every(step => step.desktopImage && step.mobileImage);
	const lightboxStep = lightboxIndex !== null ? steps[lightboxIndex] : null;

	return (
		<section className={cn('spacing-stack-l flex flex-col', className)}>
			<h2 className="text-display3 capitalize!">
				<BevSafeText>{title}</BevSafeText>
			</h2>
			<ol className="flex flex-col">
				{steps.map((step, index) => {
					const StepHeading = step.titleHeadingLevel ?? 'h3';

					return (
						<li key={index} className="spacing-inline-l flex">
							<div aria-hidden className="flex shrink-0 flex-col items-center self-stretch">
								<div className="w-px h-060 md:h-100 bg-border-default" />
								<span className="-my-040 flex shrink-0 text-text-primary [&_path]:fill-current">
									<Diamond width={16.8} height={16.8} />
								</span>
								<div className="w-px flex-1 bg-border-default" />
							</div>
							<div className="spacing-stack-l spacing-inline-l flex min-w-0 flex-1 flex-col pb-stack-l md:flex-row md:items-start">
								<div className="spacing-stack-s flex min-w-0 flex-col md:flex-1">
									<StepHeading className="text-h3 text-text-heading">
										<BevSafeText>{step.title}</BevSafeText>
									</StepHeading>
									<p className="text-body2 text-text-primary">
										<BevSafeText>{step.shortDescription}</BevSafeText>
									</p>
									{step.otherDetails && (
										<div
											className="text-body2 text-text-primary"
											dangerouslySetInnerHTML={{ __html: step.otherDetails }}
										/>
									)}
								</div>
								{showImages && step.desktopImage && step.mobileImage && (
									<button
										type="button"
										onClick={() => setLightboxIndex(index)}
										aria-label={t('expandImage')}
										className="relative block w-full shrink-0 overflow-hidden bg-surface-recessed md:w-[150px]"
									>
										<Image
											data-testid="compact-itinerary-mobile-image"
											roughSize="s"
											imagePrimitive={step.mobileImage}
											className="aspect-video w-full object-cover md:hidden"
										/>
										<Image
											data-testid="compact-itinerary-desktop-image"
											roughSize="s"
											imagePrimitive={step.desktopImage}
											className="hidden aspect-[4/5] w-full object-cover md:block"
										/>
									</button>
								)}
							</div>
						</li>
					);
				})}
			</ol>
			{showImages && lightboxStep?.desktopImage && lightboxStep?.mobileImage && (
				<Modal
					close={() => setLightboxIndex(null)}
					mobileFullScreen
					focusContainerClassNames="max-w-[92vw] md:h-[88vh]"
					contentContainerClassNames="items-center"
				>
					<Image
						data-testid="compact-itinerary-lightbox-mobile-image"
						roughSize="l"
						imagePrimitive={lightboxStep.mobileImage}
						className="max-h-[75vh] max-w-full w-auto h-auto object-contain md:hidden"
					/>
					<Image
						data-testid="compact-itinerary-lightbox-desktop-image"
						roughSize="l"
						imagePrimitive={lightboxStep.desktopImage}
						className="hidden max-h-[75vh] max-w-full w-auto h-auto object-contain md:block"
					/>
				</Modal>
			)}
		</section>
	);
}
